import { useHasAllocations } from '@/allocation/composables/useHasAllocations';
import auth from '@/auth/auth';
import {
    CreateMultiLinkDocument,
    DeleteProjectDocument,
    GetProjectDocument,
    ProjectVariantsWithAllocationsCountDocument,
    UpdateProjectDocument,
    UpdateProjectMultilinksDocument,
    UpdateProjectMutationVariables,
    UpdateProjectStatusDocument,
} from '@/generated/graphql';
import fluent from '@/plugins/fluent';
import { useMutation, useQuery } from '@vue/apollo-composable';
import { computed, Ref, toValue } from 'vue';
import { useRouter } from 'vue-router';
import { getProjectSeriesLink } from '../project';
import { ProjectStatus } from '../types';

export function useProject(projectId: Ref<string>) {
    const isAdmin = auth.isAdmin();
    const router = useRouter();

    const { hasAllocations, refetchHasAllocations } = useHasAllocations(
        ProjectVariantsWithAllocationsCountDocument,
        projectId,
    );

    const { result } = useQuery(
        GetProjectDocument,
        () => ({ id: toValue(projectId) }),
        () => ({
            enabled: !!toValue(projectId),
        }),
    );

    const project = computed(() => result.value?.project);

    const deleteProjectMutation = useMutation(DeleteProjectDocument);

    async function deleteProject() {
        if (!isAdmin || hasAllocations.value) return;
        if (!project.value) {
            throw new Error('Project to delete not loaded yet.');
        }
        if (!confirm(fluent.$t('confirm-delete-project', { name: project.value.nameShort }))) return;
        const seriesId = project.value.series?.id;

        const response = await deleteProjectMutation.mutate({ id: toValue(projectId) });
        if (response?.errors) {
            throw new Error(`Could not delete project ${response.errors}`);
        }
        if (response?.data) {
            if (seriesId) {
                router.replace(getProjectSeriesLink(seriesId));
            }
        }
    }

    const deleteDisabledExplanation = computed(() => {
        if (!isAdmin) {
            return fluent.$t('no-permission-to-edit');
        }
        if (hasAllocations.value) {
            return fluent.$t('project-has-allocations');
        }
    });

    const changeProjectStatusDisabledExplanation = computed(() => {
        return isAdmin ? undefined : fluent.$t('no-permission-to-edit');
    });

    const updateProjectStatusMutation = useMutation(UpdateProjectStatusDocument);

    async function updateProjectStatus(status: ProjectStatus) {
        if (project.value?.status === status) return;
        const response = await updateProjectStatusMutation.mutate({ id: toValue(projectId), status });
        if (response?.errors) {
            throw new Error(`Could not update project status ${response.errors}`);
        }
    }

    const updateProjectMutation = useMutation(UpdateProjectDocument);

    async function updateProject(data: UpdateProjectMutationVariables) {
        const result = await updateProjectMutation.mutate(data);
        if (result?.errors) {
            throw Error(`Error on update project: ${result.errors}`);
        }
    }

    const updateProjectMultiLinksMutation = useMutation(UpdateProjectMultilinksDocument);
    async function updateProjectMultiLinkId(fieldName: string, multiLinkId: string) {
        if (!project.value) return;
        const response = await updateProjectMultiLinksMutation.mutate({
            id: project.value.id,
            [fieldName]: multiLinkId,
        });
        if (response?.errors) {
            throw new Error(`Could not update project multilink ${fieldName}: ${response.errors}`);
        }
    }

    const createMultiLinkMutation = useMutation(CreateMultiLinkDocument);
    async function createProjectMultiLink(fieldName: string) {
        if (!project.value) return;
        const name = `${project.value.id}-${fieldName}`;
        const response = await createMultiLinkMutation.mutate({ name });
        if (response?.errors) {
            throw new Error(`Could not create Multilink for project: ${response.errors}`);
        }
        if (response?.data) {
            const multiLinkId = response.data.createMultilink?.multilink?.id;
            if (multiLinkId) {
                await updateProjectMultiLinkId(`${fieldName}Id`, multiLinkId);
                return multiLinkId;
            }
        }
    }

    return {
        project,
        deleteDisabledExplanation,
        changeProjectStatusDisabledExplanation,
        hasAllocations,
        refetchHasAllocations,
        deleteProject,
        updateProjectStatus,
        updateProjectStatusLoading: updateProjectStatusMutation.loading,
        updateProjectStatusError: updateProjectStatusMutation.error,
        updateProject,
        createProjectMultiLink,
    };
}
